import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin, MessageSquare, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const ContactSuccessPage = () => {
  return (
    <>
      <Helmet>
        <title>Thank You | Train Secure - North London College</title>
        <meta name="description" content="Thank you for contacting Train Secure. Get in touch via WhatsApp, phone, or email." />
      </Helmet>

      <div className="min-h-screen bg-[#0a0e1a] text-white">
        {/* Header */}
        <header className="border-b border-gray-800 bg-[#0a0e1a]/95 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_nlc-bootcamp/artifacts/ipcogjr5_NLC-%20TRAIN%20SECURE%20LOGO.jpg"
                alt="Train Secure"
                className="h-10 w-10 rounded-full object-cover"
              />
              <div>
                <h1 className="text-lg font-bold">Train Secure</h1>
                <p className="text-xs text-cyan-400">North London College</p>
              </div>
            </a>
          </div>
        </header>

        {/* Main Content */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            {/* Success Message */}
            <div className="text-center mb-12">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4">Thank You!</h1>
              <p className="text-lg text-gray-300 max-w-xl mx-auto">
                We've received your details and will send you helpful course information. In the meantime, feel free to reach out directly:
              </p>
            </div>

            {/* Contact Options Grid */}
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* WhatsApp Card */}
              <a
                href="https://wa.me/447435177049"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="group bg-gradient-to-br from-green-500/20 to-green-600/10 p-8 rounded-2xl border border-green-500/30 hover:border-green-500/60 transition-all hover:shadow-lg hover:shadow-green-500/10 hover:-translate-y-1"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                    <MessageSquare className="w-7 h-7 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">WhatsApp Us</h3>
                    <p className="text-green-400 font-mono text-sm">Quickest Response</p>
                  </div>
                </div>
                <p className="text-slate-300 mb-4">Get instant answers about courses, dates, and pricing. Our advisors typically respond within minutes.</p>
                <div className="flex items-center gap-2 text-green-400 font-semibold">
                  <Phone className="w-4 h-4" />
                  <span>+44 7435 177049</span>
                </div>
              </a>

              {/* Email Card */}
              <a
                href="mailto:Una@northlondoncollege.co.uk?subject=Course%20Enquiry%20-%20Train%20Secure"
                data-testid="contact-email-btn"
                className="group bg-gradient-to-br from-cyan-500/20 to-blue-600/10 p-8 rounded-2xl border border-cyan-500/30 hover:border-cyan-500/60 transition-all hover:shadow-lg hover:shadow-cyan-500/10 hover:-translate-y-1"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-cyan-500/20 flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
                    <Mail className="w-7 h-7 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Email Us</h3>
                    <p className="text-cyan-400 font-mono text-sm">Detailed Enquiries</p>
                  </div>
                </div>
                <p className="text-slate-300 mb-4">Send us your questions about course content, prerequisites, or corporate training packages.</p>
                <div className="flex items-center gap-2 text-cyan-400 font-semibold">
                  <Mail className="w-4 h-4" />
                  <span>Una@northlondoncollege.co.uk</span>
                </div>
              </a>

              {/* Phone Card - Full Width */}
              <a
                href="tel:02088088008"
                className="md:col-span-2 group bg-gradient-to-br from-purple-500/20 to-pink-600/10 p-6 rounded-2xl border border-purple-500/30 hover:border-purple-500/60 transition-all hover:shadow-lg hover:shadow-purple-500/10"
              >
                <div className="flex items-center justify-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Call Us: 0208 808 8008</h3>
                    <p className="text-purple-400 text-sm">Mon-Fri, 9am-5pm</p>
                  </div>
                </div>
              </a>
            </div>

            {/* Location */}
            <div className="mt-12 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-3 bg-slate-900/50 rounded-full border border-cyan-500/20">
                <MapPin className="w-5 h-5 text-cyan-400" />
                <span className="text-slate-300">376 High Road, Tottenham, N17 9HY</span>
              </div>
            </div>

            {/* Back to Home */}
            <div className="mt-12 text-center">
              <a href="/">
                <Button variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContactSuccessPage;
